import React, { useState, useEffect } from 'react';
import { bool, func, object, shape, string } from 'prop-types';
import { compose } from 'redux';
import { Field, Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';
import { isStripeError } from '../../util/errors';
import * as validators from '../../util/validators';
import { propTypes } from '../../util/types';
import { COMPANY, stripeAccountInitialValues } from '../../util/billing';

import {
  H4,
  Button,
  ExternalLink,
  InlineTextButton,
  FieldSelect,
  FieldRadioButton,
  FieldCheckbox,
  FieldTextInput,
  FieldBirthdayInput,
  Form,
  StripeBankAccountTokenInputField,
} from '../../components';

import css from './StripeConnectAccountForm.module.css';

const getSupportedCountryCodes = supportedCountries => supportedCountries.map(c => c.code);
const stripeCountryConfigs = (countryCode, supportedCountries) => {
  const country = supportedCountries.find(c => c.code === countryCode);

  if (!country) {
    throw new Error(`Country code not found in Stripe config ${countryCode}`);
  }
  return country;
};

const countryCurrency = (countryCode, supportedCountries) => {
  const country = stripeCountryConfigs(countryCode, supportedCountries);
  return country.currency;
};

// Hidden input field
const FieldHidden = props => {
  const { name } = props;
  return (
    <Field id={name} name={name} type="hidden" className={css.unitTypeHidden}>
      {fieldRenderProps => <input {...fieldRenderProps?.input} />}
    </Field>
  );
};

const CreateStripeAccountFields = props => {
  const routeConfiguration = useRouteConfiguration();
  const {
    disabled,
    countryLabel,
    showAsRequired,
    form,
    values,
    intl,
    currentUserId,
    listingAddress,
    marketplaceRootURL,
    supportedCountries,
    defaultMCC,
  } = props;

  /*
  We pass some default values to Stripe when creating a new Stripe account in order to reduce couple of steps from Connect Onboarding form.
  - businessProfileURL: user's profile URL
  - businessProfileMCC: default MCC code from stripe-config.js
  - accountToken (https://stripe.com/docs/connect/account-tokens) with following information:
    * accountType: individual or business
    * tos_shown_and_accepted: true
  Only country and bank account token are mandatory values. If you decide to remove the additional default values listed here, remember to update the `createStripeAccount` function in `ducks/stripeConnectAccount.duck.js`.
  */

  useEffect(() => {
    // Use user profile page as business_url on this marketplace
    // or just fake it if it's dev environment using Stripe test endpoints
    // because Stripe will not allow passing a localhost URL
    const hasBusinessURL = values && values.businessProfileURL;
    if (!hasBusinessURL && currentUserId) {
      const pathToProfilePage = uuid =>
        createResourceLocatorString('ProfilePage', routeConfiguration, { id: uuid }, {});
      const hasMarketplaceRootURL = !!marketplaceRootURL;
      const rootUrl = hasMarketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : null;
      const defaultBusinessURL =
        hasMarketplaceRootURL && !rootUrl.includes('localhost')
          ? `${rootUrl}${pathToProfilePage(currentUserId.uuid)}`
          : `https://test-marketplace.com${pathToProfilePage(currentUserId.uuid)}`;
      form.change('businessProfileURL', defaultBusinessURL);
    }

    const hasMCC = values && values.businessProfileMCC;
    // Use default merchant category code (MCC) from stripe-config.js
    if (!hasMCC && defaultMCC) {
      form.change('businessProfileMCC', defaultMCC);
    }
  }, []);

  const country = values.country;

  return (
    <div className={css.sectionContainer}>
      <H4 as="h3">
        <FormattedMessage id="StripeConnectAccountForm.accountTypeTitle" />
      </H4>
      <div className={css.radioButtonRow}>
        <FieldRadioButton
          id="individual"
          name="accountType"
          label={intl.formatMessage({
            id: 'StripeConnectAccountForm.individualAccount',
          })}
          value="individual"
          showAsRequired={showAsRequired}
        />
        <FieldRadioButton
          id="company"
          name="accountType"
          label={intl.formatMessage({ id: 'StripeConnectAccountForm.companyAccount' })}
          value="company"
          showAsRequired={showAsRequired}
        />
      </div>

      <FieldHidden name="businessProfileURL" />
      <FieldHidden name="businessProfileMCC" />

      <FieldSelect
        id="country"
        name="country"
        disabled={disabled}
        className={css.selectCountry}
        autoComplete="country"
        label={countryLabel}
        validate={validators.required(
          intl.formatMessage({
            id: 'StripeConnectAccountForm.countryRequired',
          })
        )}
      >
        <option disabled value="">
          {intl.formatMessage({ id: 'StripeConnectAccountForm.countryPlaceholder' })}
        </option>
        {getSupportedCountryCodes(supportedCountries).map(c => (
          <option key={c} value={c}>
            {intl.formatMessage({ id: `StripeConnectAccountForm.countryNames.${c}` })}
          </option>
        ))}
      </FieldSelect>

      {/* Con empresa este campo no se pinta: el documento de la persona lo pide
          `companyOwnerDni`, y aquí `dni` no iría a ninguna parte —Stripe solo lo
          recibe en una cuenta de persona física (`individual.id_number`)—, así
          que era el mismo dato preguntado dos veces. */}
      {values.accountType === COMPANY ? null : (
        <FieldTextInput
          id="dni"
          name="dni"
          type="text"
          label={intl.formatMessage({ id: 'StripeConnectAccountForm.dniLabel' })}
          placeholder={intl.formatMessage({ id: 'StripeConnectAccountForm.dniPlaceholder' })}
          disabled={disabled}
          autoComplete="off"
          validate={validators.composeValidators(
            validators.required(intl.formatMessage({ id: 'StripeConnectAccountForm.dniRequired' })),
            validators.validSpanishTaxIdUnless(
              intl.formatMessage({ id: 'StripeConnectAccountForm.taxIdInvalid' }),
              allValues => allValues.dniForeign
            )
          )}
        />
      )}
      {values.accountType === COMPANY ? null : (
        <FieldCheckbox
          id="dniForeign"
          name="dniForeign"
          label={intl.formatMessage({ id: 'StripeConnectAccountForm.foreignDocumentLabel' })}
          disabled={disabled}
        />
      )}
      {/* Datos de facturación de la empresa.
          Ojo: estos tres campos NO viajan a Stripe — Stripe verifica a la
          sociedad y a su representante por su cuenta. Los pedimos aquí porque es
          el momento en que el anfitrión está dando sus datos de cobro, y porque
          si no los pedimos no los tenemos: sin ellos su factura de comisión sale
          a su nombre como particular.

          El DNI del titular es un campo aparte del CIF a propósito. El CIF es de
          la sociedad; el aviso al asegurador necesita el documento de la persona
          y lo imprime bajo el rótulo "DNI/NIE anfitrión". Ver src/util/billing.js. */}
      {values.accountType === COMPANY ? (
        <>
          <FieldTextInput
            id="companyLegalName"
            name="companyLegalName"
            type="text"
            autoComplete="organization"
            label={intl.formatMessage({ id: 'StripeConnectAccountForm.companyLegalNameLabel' })}
            placeholder={intl.formatMessage({
              id: 'StripeConnectAccountForm.companyLegalNamePlaceholder',
            })}
            disabled={disabled}
            validate={validators.required(
              intl.formatMessage({ id: 'StripeConnectAccountForm.companyLegalNameRequired' })
            )}
          />
          <FieldTextInput
            id="companyTaxId"
            name="companyTaxId"
            type="text"
            autoComplete="off"
            label={intl.formatMessage({ id: 'StripeConnectAccountForm.companyTaxIdLabel' })}
            placeholder={intl.formatMessage({
              id: 'StripeConnectAccountForm.companyTaxIdPlaceholder',
            })}
            disabled={disabled}
            validate={validators.composeValidators(
              validators.required(
                intl.formatMessage({ id: 'StripeConnectAccountForm.companyTaxIdRequired' })
              ),
              validators.validSpanishTaxId(
                intl.formatMessage({ id: 'StripeConnectAccountForm.taxIdInvalid' })
              )
            )}
          />
          <FieldTextInput
            id="companyOwnerDni"
            name="companyOwnerDni"
            type="text"
            autoComplete="off"
            label={intl.formatMessage({ id: 'StripeConnectAccountForm.companyOwnerDniLabel' })}
            placeholder={intl.formatMessage({ id: 'StripeConnectAccountForm.dniPlaceholder' })}
            disabled={disabled}
            validate={validators.composeValidators(
              validators.required(
                intl.formatMessage({ id: 'StripeConnectAccountForm.companyOwnerDniRequired' })
              ),
              validators.validSpanishTaxIdUnless(
                intl.formatMessage({ id: 'StripeConnectAccountForm.taxIdInvalid' }),
                allValues => allValues.companyOwnerDniForeign
              )
            )}
          />
          <FieldCheckbox
            id="companyOwnerDniForeign"
            name="companyOwnerDniForeign"
            label={intl.formatMessage({ id: 'StripeConnectAccountForm.foreignDocumentLabel' })}
            disabled={disabled}
          />
        </>
      ) : null}

      <FieldBirthdayInput
        id="dateOfBirth"
        name="dateOfBirth"
        label={intl.formatMessage({ id: 'StripeConnectAccountForm.dateOfBirthLabel' })}
        placeholder={intl.formatMessage({ id: 'ProfileSettingsForm.birthdayPlaceholder' })}
        disabled={disabled}
        /*
          El validador existe siempre y decide por el valor. Pasarlo o quitarlo
          según `accountType` dejaba el error pegado: al cambiar el radio,
          final-form valida SÍNCRONAMENTE con el validador de antes —React aún no
          ha re-renderizado— y un re-render por sí solo no vuelve a validar. El
          resultado era "La fecha de nacimiento es obligatoria" en rojo con
          "Represento una compañía" seleccionado.
        */
        validate={(value, allValues) =>
          allValues.accountType === COMPANY
            ? undefined
            : validators.composeValidators(
                validators.required(
                  intl.formatMessage({ id: 'StripeConnectAccountForm.dateOfBirthRequired' })
                ),
                validators.dateValid(
                  intl.formatMessage({ id: 'StripeConnectAccountForm.dateOfBirthInvalid' })
                )
              )(value)
        }
      />

      {/*
        La dirección fiscal es lo ÚNICO que falta tras este paso, para un
        particular y para una empresa por igual. Un anfitrión que publica un
        anuncio acaba de darnos una, así que se ofrece en un clic en vez de pedir
        cinco campos más en el paso del dinero.

        Desmarcada a propósito: la piscina no tiene por qué estar donde su dueño
        tiene el domicilio fiscal, y una dirección que nadie eligió es peor que
        ninguna. Quien no la marque la verá pedida en "Necesita tu atención".
      */}
      {listingAddress?.line1 ? (
        <FieldCheckbox
          id="useListingAddress"
          name="useListingAddress"
          label={intl.formatMessage(
            { id: 'StripeConnectAccountForm.useListingAddressLabel' },
            {
              address: [listingAddress.line1, listingAddress.city].filter(Boolean).join(', '),
            }
          )}
          disabled={disabled}
        />
      ) : null}

      {country ? (
        <StripeBankAccountTokenInputField
          className={css.bankDetailsStripeField}
          disabled={disabled}
          name="bankAccountToken"
          formName="StripeConnectAccountForm"
          country={country}
          currency={countryCurrency(country, supportedCountries)}
          validate={validators.required(' ')}
        />
      ) : null}
    </div>
  );
};

const UpdateStripeAccountFields = props => {
  const {
    disabled,
    countryLabel,
    savedCountry,
    showCardUpdateInput,
    submitInProgress,
    setShowCardUpdateInput,
    stripeBankAccountLastDigits,
    supportedCountries,
  } = props;

  return (
    <div className={css.savedInformation}>
      <label className={css.accountInformationTitle}>{countryLabel}</label>
      <div className={css.savedCountry}>
        <FormattedMessage id={`StripeConnectAccountForm.countryNames.${savedCountry}`} />
      </div>
      <label className={css.accountInformationTitle}>
        <FormattedMessage id="StripeConnectAccountForm.bankAccountLabel" />
      </label>

      {showCardUpdateInput && savedCountry ? (
        <StripeBankAccountTokenInputField
          className={css.bankDetailsStripeField}
          disabled={disabled}
          name="bankAccountToken"
          formName="StripeConnectAccountForm"
          country={savedCountry}
          currency={countryCurrency(savedCountry, supportedCountries)}
          validate={validators.required(' ')}
        />
      ) : !submitInProgress ? (
        <InlineTextButton
          className={css.savedBankAccount}
          onClick={() => setShowCardUpdateInput(true)}
        >
          •••••••••••••••••••••••• {stripeBankAccountLastDigits}
        </InlineTextButton>
      ) : null}
    </div>
  );
};

const ErrorsMaybe = props => {
  const { stripeAccountError, stripeAccountLinkError } = props;

  const errorMessage = isStripeError(stripeAccountError) ? (
    <FormattedMessage
      id="StripeConnectAccountForm.createStripeAccountFailedWithStripeError"
      values={{ stripeMessage: stripeAccountError.apiErrors[0].meta.stripeMessage }}
    />
  ) : stripeAccountError ? (
    <FormattedMessage id="StripeConnectAccountForm.createStripeAccountFailed" />
  ) : isStripeError(stripeAccountLinkError) ? (
    <FormattedMessage
      id="StripeConnectAccountForm.createStripeAccountLinkFailedWithStripeError"
      values={{ stripeMessage: stripeAccountLinkError.apiErrors[0].meta.stripeMessage }}
    />
  ) : stripeAccountLinkError ? (
    <FormattedMessage id="StripeConnectAccountForm.createStripeAccountLinkFailed" />
  ) : null;

  return errorMessage ? <div className={css.error}>{errorMessage}</div> : null;
};

const StripeConnectAccountFormComponent = props => {
  const [showCardUpdateInput, setShowCardUpdateInput] = useState(false);
  const config = useConfiguration();
  const { onSubmit, ...restOfProps } = props;
  const isUpdate = props.stripeConnected;
  const stripePublishableKey = config.stripe.publishableKey;
  const supportedCountries = config.stripe.supportedCountries;

  // Los datos fiscales ya los tenemos si el anfitrión pasó por Datos de
  // facturación; pedírselos otra vez sería teclear lo mismo dos veces.
  const initialValues = { country: 'ES', ...stripeAccountInitialValues(props.currentUser) };

  return (
    <FinalForm
      initialValues={initialValues}
      {...restOfProps}
      onSubmit={values =>
        onSubmit(
          { ...values, listingAddress: props.listingAddress, stripePublishableKey },
          isUpdate
        )
      }
      mutators={{
        ...arrayMutators,
      }}
      render={fieldRenderProps => {
        const {
          rootClassName,
          className,
          children,
          stripeAccountError,
          stripeAccountLinkError,
          disabled,
          handleSubmit,
          inProgress,
          intl,
          invalid,
          pristine,
          ready,
          savedCountry,
          stripeAccountFetched,
          stripeBankAccountLastDigits,
          submitButtonText,
          submitFailed,
          form,
          values,
          stripeConnected,
          currentUser,
          listingAddress,
        } = fieldRenderProps;

        const accountDataLoaded = stripeConnected && stripeAccountFetched && savedCountry;
        const submitInProgress = inProgress;
        const submitDisabled = pristine || invalid || disabled || submitInProgress;

        const handleFormSubmit = event => {
          // Close the bank account form when clicking "save details"
          setShowCardUpdateInput(false);
          handleSubmit(event);
        };

        const countryLabel = intl.formatMessage({ id: 'StripeConnectAccountForm.countryLabel' });
        const classes = classNames(rootClassName || css.root, className, {
          [css.disabled]: disabled,
        });

        const showAsRequired = submitFailed;

        const currentUserId = currentUser ? currentUser.id : null;

        // If the user doesn't have Stripe connected account,
        // show fields for country and bank account.
        // Otherwise, show only possibility the edit bank account
        // because Stripe doesn't allow user to change the country
        const stripeAccountFields = !stripeConnected ? (
          <CreateStripeAccountFields
            stripeConnected={stripeConnected}
            disabled={disabled}
            showAsRequired={showAsRequired}
            countryLabel={countryLabel}
            supportedCountries={supportedCountries}
            marketplaceRootURL={config.marketplaceRootURL}
            defaultMCC={config.stripe.defaultMCC}
            currentUserId={currentUserId}
            listingAddress={listingAddress}
            form={form}
            values={values}
            intl={intl}
          />
        ) : (
          <UpdateStripeAccountFields
            disabled={disabled}
            countryLabel={countryLabel}
            supportedCountries={supportedCountries}
            savedCountry={savedCountry}
            stripeBankAccountLastDigits={stripeBankAccountLastDigits}
            showCardUpdateInput={showCardUpdateInput}
            values={values}
            submitInProgress={submitInProgress}
            setShowCardUpdateInput={setShowCardUpdateInput}
            intl={intl}
          />
        );

        const stripeConnectedAccountTermsLink = (
          <ExternalLink href="https://stripe.com/connect-account/legal" className={css.termsLink}>
            <FormattedMessage id="StripeConnectAccountForm.stripeConnectedAccountTermsLink" />
          </ExternalLink>
        );

        // Don't show the submit button while fetching the Stripe account data
        const submitButtonMaybe =
          !stripeConnected || accountDataLoaded ? (
            <>
              <p className={css.termsText}>
                <FormattedMessage
                  id="StripeConnectAccountForm.stripeToSText"
                  values={{ stripeConnectedAccountTermsLink }}
                />
              </p>

              <Button
                type="submit"
                inProgress={submitInProgress}
                disabled={submitDisabled}
                ready={ready}
              >
                {submitButtonText || (
                  <FormattedMessage id="StripeConnectAccountForm.submitButtonText" />
                )}
              </Button>
            </>
          ) : null;

        // If the Stripe publishable key is not set up, don't show the form
        return config.stripe.publishableKey ? (
          <Form className={classes} onSubmit={handleFormSubmit}>
            {!stripeConnected || accountDataLoaded ? (
              stripeAccountFields
            ) : (
              <div className={css.savedInformation}>
                <FormattedMessage id="StripeConnectAccountForm.loadingStripeAccountData" />
              </div>
            )}

            <ErrorsMaybe
              stripeAccountError={stripeAccountError}
              stripeAccountLinkError={stripeAccountLinkError}
            />

            {children}

            {submitButtonMaybe}
          </Form>
        ) : (
          <div className={css.missingStripeKey}>
            <FormattedMessage id="StripeConnectAccountForm.missingStripeKey" />
          </div>
        );
      }}
    />
  );
};

StripeConnectAccountFormComponent.defaultProps = {
  className: null,
  currentUser: null,
  stripeAccountError: null,
  disabled: false,
  inProgress: false,
  ready: false,
  savedCountry: null,
  stripeBankAccountLastDigits: null,
  submitButtonText: null,
  fieldRenderProps: null,
};

StripeConnectAccountFormComponent.propTypes = {
  currentUser: propTypes.currentUser,
  // La dirección del anuncio recién publicado, para ofrecerla en un clic.
  listingAddress: object,
  className: string,
  stripeAccountError: object,
  disabled: bool,
  inProgress: bool,
  ready: bool,
  savedCountry: string,
  stripeBankAccountLastDigits: string,
  stripeAccountFetched: bool.isRequired,
  submitButtonText: string,
  fieldRenderProps: shape({
    handleSubmit: func,
    invalid: bool,
    pristine: bool,
    values: object,
  }),

  // from injectIntl
  intl: intlShape.isRequired,
};

const StripeConnectAccountForm = compose(injectIntl)(StripeConnectAccountFormComponent);

export default StripeConnectAccountForm;
